/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-empty-object-type */
// 2) Third-party library imports
import {
    clone,
    concat,
    get,
    isEqual,
    keyBy,
    merge,
    uniqWith,
    values
} from 'lodash-es';
import {
    createSlice,
    PayloadAction,
    createAction,
    createSelector
} from '@reduxjs/toolkit';

// 3a) Constants
import {
    CUSTOMERS_PAGE,
    DETAILS_TAB,
    INVOICES_PAGE
} from '../../config/tableAndPageConstants';
import { conversationTableFilterOptions } from '../../constants/common';
import {
    invoiceChangesTableFilterOptions,
    invoicesSortByOptions,
    invoicesStateFilterOptions,
} from '../../constants/invoicesSortAndFilters';

// 3c) Project-specific modules - components / types
import {
    BatchPaymentPlanConfigModel,
    BatchPaymentPlanFormModel,
    GetInvoiceChangesRequestPayload,
    GetInvoicesRequestPayload,
    Invoice,
    InvoiceAddCommentRequestPayload,
    InvoicePauseResumeRequestPayload,
    InvoicesFetchedForOrganisationPayload,
    InvoicesState
} from './types';
import {
    Conversation,
    GetConversationRequestPayload,
    PageData
} from '../common/types';
import { ConversationLinesFetchedForOrganisationPayload } from '../conversationLine/types';
import { ChangeLinesFetchedForOrganisationPayload } from '../changes/types';
import { Customer } from '../customers/types';
import type { RootState } from '../storeTypes';

// 3d) Utility functions
import { DynamicObject } from '../../utils/commonInterfaces';

const actionPrefix = 'invoice';

// Type-safe initialState!
export const initialState: InvoicesState = {
    loading: false,
    errorMessages: [],
    data: [],
    pageData: {
        pageSize: INVOICES_PAGE.pageSize,
        currentPage: 0,
        hasNextPage: false,
    },
    filters: {
        InvoiceNumber: '',
        Customer: '',
        CustomerCode: '',
        CreatedDateMin: '',
        CreatedDateMax: '',
        DueDateMin: '',
        DueDateMax: '',
        AmountType: '',
        AmountValue: '',
        AmountOperator: '',
    },
    tableFilter: undefined,
    invoiceState: get(invoicesStateFilterOptions, `${0}.value`),
    sortBy: invoicesSortByOptions[0].value, // 2 for Invoice number
    sortAscending: true,
    activeData: {
        record: {},
        loading: false,
        selectedId: null,
        errorMessages: [],
        conversation: {
            loading: false,
            errorMessages: [],
            data: [],
            pageData: {
                pageSize: DETAILS_TAB.CONVERSATION_TIMELINE.pageSize,
                currentPage: 0,
                hasNextPage: false,
            },
            filters: {},
            conversationTableFilter: get(
                conversationTableFilterOptions,
                '0.value'
            ),
            sortBy: 'Created Date Time',
            sortAscending: false,
        },
        invoiceChanges: {
            loading: false,
            errorMessages: [],
            data: [],
            pageData: {
                pageSize: DETAILS_TAB.INVOICE_CHANGES_TIMELINE.pageSize,
                currentPage: 0,
                hasNextPage: false,
            },
            filters: {},
            invoiceChangesTableFilter: get(
                invoiceChangesTableFilterOptions,
                '0.value'
            ),
            sortBy: 'Created Date Time',
            sortAscending: false,
        },
    },
    compactView: false,
    batchPaymentPlan: {
        invoices: {
            panelFilters: {
                InvoiceCode: undefined,
                CustomerCode: undefined,
                Customer: undefined
            },
            loading: false,
            data: [],
            pageData: {
                pageSize: INVOICES_PAGE.pageSize,
                currentPage: 0,
                hasNextPage: false,
            },
        },
        customers: {
            panelFilters: {
                InvoiceCode: undefined,
                CustomerCode: undefined,
                Customer: undefined
            },
            loading: false,
            data: [],
            pageData: {
                pageSize: CUSTOMERS_PAGE.pageSize,
                currentPage: 0,
                hasNextPage: false,
            },
        },
        paymentPlanConfigs: {
            loading: false,
            data: [],
            selectedTemplateId: undefined
        },
        formData: {
            customerIds: [],
            excludedItems: [],
            invoiceItems: [],
            paymentPlanConfig: {
                ConfigId: undefined,
                InstalmentType: undefined,
                Instalments: undefined,
                ScheduleDates: [],
                PayPercent: [],
                SplitInstalmentDateRange: [],
                Name: undefined,
                CutoffDate: undefined,
                StartDelay: undefined,
                FrequencyGap: undefined,
                SpecificPaymentDate: undefined,
                StartTiming: undefined
            },
            requestFromPage: undefined,
            skipCustomerNotification: false,
            isIndividualInvoice: false,
        },
        reviewingBatchPaymentPlan: {},
        reviewingInvoice: {
            loading: false,
            data: [],
        },
        customerManagementFilter: undefined,
        invoiceManagementFilter: undefined
    }
};

// Thanks to Redux 4's much simpler typings, we can take away a lot of typings on the reducer side,
// everything will remain type-safe.
const invoiceSlice = createSlice({
    name: actionPrefix,
    initialState,
    reducers: {
        getInvoicesForOrganisationRequestAction: (
            state,
            _action: PayloadAction<GetInvoicesRequestPayload>
        ) => {
            state.loading = true;
        },
        getInvoicesRequestAction: (
            state,
            _action: PayloadAction<GetInvoicesRequestPayload>
        ) => {
            state.loading = true;
        },
        getInvoicesSuccessAction: (
            state,
            action: PayloadAction<{
                data: Invoice[];
                pageData: PageData;
                mergeData?: boolean;
            }>
        ) => {
            if (action.payload.mergeData === true) {
                state.data = values(
                    merge(
                        keyBy(state.data, 'Id'),
                        keyBy(action.payload.data, 'Id')
                    )
                );
            } else if (action.payload.pageData.currentPage === 0) {
                state.data = action.payload.data;
            } else {
                const existingData = state.data || [];
                state.data = uniqWith([...existingData, ...action.payload.data], isEqual);
            }

            state.loading = false;
            state.pageData = action.payload.pageData;
            state.errorMessages = [];
        },
        getInvoicesErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.loading = false;
            state.data = initialState.data;
            state.errorMessages = action.payload;
        },
        updateInvoicesFiltersAction: (
            state,
            action: PayloadAction<GetInvoicesRequestPayload['filters']>
        ) => {
            state.filters = action.payload;
        },
        updateInvoicesSortByAndStateAction: (
            state,
            action: PayloadAction<{
                sortBy: GetInvoicesRequestPayload['sortBy'];
                sortAscending: boolean;
                invoiceState: string;
            }>
        ) => {
            state.sortBy = action.payload.sortBy;
            state.sortAscending = action.payload.sortAscending;
            state.invoiceState = action.payload.invoiceState;
        },
        updateInvoicesTableFilterAction: (
            state,
            action: PayloadAction<string>
        ) => {
            state.tableFilter = action.payload;
        },
        clearInvoicesStateAllTableFiltersAction: (state) => {
            state.filters = initialState.filters;
            state.sortBy = initialState.sortBy;
            state.sortAscending = initialState.sortAscending;
            state.invoiceState = initialState.invoiceState;
            state.tableFilter = initialState.tableFilter;
        },
        clearInvoicesStateDataAction: (state) => {
            Object.assign(state, initialState);
        },
        clearInvoicesListData: (state) => {
            state.loading = false;
            state.data = [];
            state.pageData = initialState.pageData;
            state.errorMessages = [];
        },
        setSelectedInvoiceIdRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    invoiceId: string | null;
                    callback: () => void
                }>
            ) => {
                state.activeData.selectedId = initialState.activeData.selectedId;
            },
            prepare: (
                invoiceId: string | null,
                callback: () => void
            ) => {
                return {
                    payload: { invoiceId, callback }
                }
            }
        },
        setSelectedInvoiceIdSuccessAction: (
            state,
            action: PayloadAction<string | null>
        ) => {
            state.activeData.selectedId = action.payload;
        },
        getOrganisationInvoiceDataRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    invoiceId: string,
                    region: string,
                    isUsingCloudImportType: boolean,
                    companyId: string,
                    conversionCurrency: number,
                    currencies: DynamicObject[]
                }>
            ) => {
                state.activeData.record = initialState.activeData.record;
                state.activeData.loading = true;
            },
            prepare: (
                invoiceId: string,
                region: string,
                isUsingCloudImportType: boolean,
                companyId: string,
                conversionCurrency: number,
                currencies: DynamicObject[]
            ) => {
                return {
                    payload: {
                        invoiceId,
                        region,
                        isUsingCloudImportType,
                        companyId,
                        conversionCurrency,
                        currencies
                    }
                }
            }
        },
        getInvoiceDataRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    invoiceId: string,
                    isUsingCloudImportType: boolean,
                    isInvoicePanelView: boolean
                }>
            ) => {
                state.activeData.record = initialState.activeData.record;
                state.activeData.loading = true;
            },
            prepare: (
                invoiceId: string,
                isUsingCloudImportType: boolean,
                isInvoicePanelView: boolean = false
            ) => {
                return {
                    payload: {
                        invoiceId,
                        isUsingCloudImportType,
                        isInvoicePanelView
                    }
                }
            }
        },
        getInvoiceDataSuccessAction: (
            state,
            action: PayloadAction<{ record: {} }>
        ) => {
            state.activeData.record = action.payload.record;
            state.activeData.loading = false;
            state.activeData.errorMessages = [];
        },
        getInvoiceDataErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.activeData.record = initialState.activeData.record;
            state.activeData.loading = false;
            state.activeData.errorMessages = action.payload;
        },
        clearInvoiceDataAction: (state) => {
            state.activeData = initialState.activeData;
        },
        getOrganisationInvoiceConversationRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    payload: GetConversationRequestPayload;
                    region: string;
                    companyIds: string[];
                }>
            ) => {
                state.activeData.conversation.loading = true;
            },
            prepare: (
                payload: GetConversationRequestPayload,
                region: string,
                companyIds: string[]
            ) => {
                return {
                    payload: { payload, region, companyIds }
                };
            }
        },
        getInvoiceConversationRequestAction: (
            state,
            _action: PayloadAction<GetConversationRequestPayload>
        ) => {
            state.activeData.conversation.loading = true;
        },
        getInvoiceConversationSuccessAction: (
            state,
            action: PayloadAction<{
                data: Conversation[];
                pageData: PageData;
            }>
        ) => {
            if (action.payload.pageData.currentPage === 0) {
                state.activeData.conversation.data = action.payload.data;
            } else {
                const existingData = state.activeData.conversation.data || [];
                state.activeData.conversation.data = uniqWith([...existingData, ...action.payload.data], isEqual);
            }

            state.activeData.conversation.loading = false;
            state.activeData.conversation.pageData = action.payload.pageData;
            state.activeData.conversation.errorMessages = [];
        },
        getInvoiceConversationErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.activeData.conversation.loading = false;
            state.activeData.conversation.data = initialState.activeData.conversation.data;
            state.activeData.conversation.errorMessages = action.payload;
        },
        updateInvoiceConversationFiltersAction: (
            state,
            action: PayloadAction<GetConversationRequestPayload['filters']>
        ) => {
            state.activeData.conversation.filters = action.payload;
        },
        updateInvoiceConversationTableFilterAction: (
            state,
            action: PayloadAction<string>
        ) => {
            state.activeData.conversation.conversationTableFilter = action.payload;
        },
        updateInvoiceConversationSortByAction: (
            state,
            action: PayloadAction<{
                sortBy: GetConversationRequestPayload['sortBy'];
                sortAscending: boolean;
            }>
        ) => {
            state.activeData.conversation.sortBy = action.payload.sortBy;
            state.activeData.conversation.sortAscending = action.payload.sortAscending;
        },
        getOrganisationInvoiceChangesRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    payload: GetInvoiceChangesRequestPayload;
                    region: string;
                    companyIds: string[];
                }>
            ) => {
                state.activeData.invoiceChanges.loading = true;
            },
            prepare: (
                payload: GetInvoiceChangesRequestPayload,
                region: string,
                companyIds: string[]
            ) => {
                return {
                    payload: { payload, region, companyIds }
                };
            }
        },
        getInvoiceChangesRequestAction: (
            state,
            _action: PayloadAction<GetInvoiceChangesRequestPayload>
        ) => {
            state.activeData.invoiceChanges.loading = true;
        },
        getInvoiceChangesSuccessAction: (
            state,
            action: PayloadAction<{
                data: [];
                pageData: PageData;
            }>
        ) => {
            if (action.payload.pageData.currentPage === 0) {
                state.activeData.invoiceChanges.data = action.payload.data;
            } else {
                const existingData = state.activeData.invoiceChanges.data || [];
                state.activeData.invoiceChanges.data = uniqWith([...existingData, ...action.payload.data], isEqual);
            }

            state.activeData.invoiceChanges.loading = false;
            state.activeData.invoiceChanges.pageData = action.payload.pageData;
            state.activeData.invoiceChanges.errorMessages = [];
        },
        getInvoiceChangesErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.activeData.invoiceChanges.loading = false;
            state.activeData.invoiceChanges.data = initialState.activeData.invoiceChanges.data;
            state.activeData.invoiceChanges.errorMessages = action.payload;
        },
        updateInvoiceChangesFiltersAction: (
            state,
            action: PayloadAction<GetInvoiceChangesRequestPayload['filters']>
        ) => {
            state.activeData.invoiceChanges.filters = action.payload;
        },
        updateInvoiceChangesTableFilterAction: (
            state,
            action: PayloadAction<string>
        ) => {
            state.activeData.invoiceChanges.invoiceChangesTableFilter = action.payload;
        },
        updateInvoiceChangesSortByAction: (
            state,
            action: PayloadAction<{
                sortBy: GetInvoiceChangesRequestPayload['sortBy'];
                sortAscending: boolean;
            }>
        ) => {
            state.activeData.invoiceChanges.sortBy = action.payload.sortBy;
            state.activeData.invoiceChanges.sortAscending = action.payload.sortAscending;
        },
        setTableCompactViewAction: (
            state,
            action: PayloadAction<boolean>
        ) => {
            state.compactView = action.payload;
        },
        getInvoiceChangeLinesSuccessAction: (
            state,
            action: PayloadAction<{
                data: Invoice[];
                pageData: PageData;
            }>
        ) => {
            if (action.payload.pageData.currentPage === 0) {
                state.activeData.invoiceChanges.data = action.payload.data;
            } else {
                state.activeData.invoiceChanges.data = uniqWith(
                    [
                        ...state.activeData.invoiceChanges.data,
                        ...action.payload.data
                    ],
                    isEqual
                );
            }

            state.activeData.invoiceChanges.loading = false;
            state.activeData.invoiceChanges.pageData = action.payload.pageData
            state.activeData.invoiceChanges.errorMessages = [];
        },
        getCustomerInvoicesForCompanyRequestAction: (
            state,
            _action: PayloadAction<GetInvoicesRequestPayload>
        ) => {
            state.batchPaymentPlan.reviewingInvoice.loading = true;
        },
        getCustomersHaveNoPaymentPlanRequestAction: (
            state,
            _action: PayloadAction<any>
        ) => {
            state.batchPaymentPlan.customers.loading = true;
        },
        getInvoicesHaveNoPaymentPlanRequestAction: (
            state,
            _action: PayloadAction<any>
        ) => {
            state.batchPaymentPlan.invoices.loading = true;
        },
        getPaymentPlanConfigTemplateRequestAction: (
            state,
            _action: PayloadAction
        ) => {
            state.batchPaymentPlan.paymentPlanConfigs.loading = true;
        },
        updateBatchPaymentPlanFormDataAction: (
            state,
            action: PayloadAction<BatchPaymentPlanFormModel>
        ) => {
            state.batchPaymentPlan.formData = action.payload;
        },
        updateInvoicesHaveNoPaymentPlanDataAction: (
            state,
            action: PayloadAction<Invoice[]>
        ) => {
            state.batchPaymentPlan.reviewingInvoice.loading = false;
            state.batchPaymentPlan.reviewingInvoice.data = action.payload;
        },
        getCustomersHaveNoPaymentPlanFailedAction: (
            state,
            _action: PayloadAction
        ) => {
            state.batchPaymentPlan.customers.loading = false;
            state.batchPaymentPlan.customers.data = initialState.batchPaymentPlan.customers.data;
        },
        getCustomersHaveNoPaymentPlanSuccessfullyAction: (
            state,
            action: PayloadAction<{
                data: Customer[];
                pageData: PageData;
            }>
        ) => {
            const { data, pageData } = action.payload;

            if (action.payload.pageData.currentPage === 0) {
                state.batchPaymentPlan.customers.data = data;
            } else {
                state.batchPaymentPlan.customers.data = uniqWith(
                    concat(state.batchPaymentPlan.customers.data, data),
                    isEqual
                );
            }

            state.batchPaymentPlan.customers.loading = false;
            state.batchPaymentPlan.customers.pageData = pageData;
        },
        updateBatchPaymentPlanCustomerFilters: (
            state,
            action: PayloadAction<any>
        ) => {
            state.batchPaymentPlan.customerManagementFilter = action.payload;
        },
        updateBatchPaymentPlanInvoiceFilters: (
            state,
            action: PayloadAction<any>
        ) => {
            state.batchPaymentPlan.invoiceManagementFilter = action.payload;
        },
        getInvoicesHaveNoPaymentPlanSuccessfullyAction: (
            state,
            action: PayloadAction<{
                data: Invoice[];
                pageData: PageData;
            }>
        ) => {
            const { data, pageData } = action.payload;

            if (action.payload.pageData.currentPage === 0) {
                state.batchPaymentPlan.invoices.data = data;
            } else {
                state.batchPaymentPlan.invoices.data = uniqWith(
                    concat(state.batchPaymentPlan.invoices.data, data),
                    isEqual
                );
            }

            state.batchPaymentPlan.invoices.loading = false;
            state.batchPaymentPlan.invoices.pageData = pageData;
        },
        getInvoicesHaveNoPaymentPlanFailedAction: (
            state,
            _action: PayloadAction
        ) => {
            state.batchPaymentPlan.invoices.loading = false;
            state.batchPaymentPlan.invoices.data = initialState.batchPaymentPlan.invoices.data;
        },
        getPaymentPlanConfigTemplateSuccessfullyAction: (
            state,
            action: PayloadAction<{
                data: BatchPaymentPlanConfigModel[]
            }>
        ) => {
            state.batchPaymentPlan.paymentPlanConfigs.loading = false;
            state.batchPaymentPlan.paymentPlanConfigs.data = action.payload.data;
        },
        getPaymentPlanConfigTemplateFailedAction: (
            state,
            _action: PayloadAction
        ) => {
            state.batchPaymentPlan.paymentPlanConfigs = initialState.batchPaymentPlan.paymentPlanConfigs;
        },
        resetBatchPaymentPlanStateAction: (
            state,
            _action: PayloadAction
        ) => {
            state.batchPaymentPlan = initialState.batchPaymentPlan;
        },
        selectPaymentPlanTemplateAction: (
            state,
            action: PayloadAction<string>
        ) => {
            state.batchPaymentPlan.paymentPlanConfigs.selectedTemplateId = action.payload;
        },
        applyReviewingBatchPaymentPlanAction: (
            state,
            action: PayloadAction<{}>
        ) => {
            state.batchPaymentPlan.reviewingBatchPaymentPlan = action.payload;
        },
        resetInvoiceHaveNoPaymentPlanFiltersAction: (
            state,
            _action: PayloadAction
        ) => {
            state.batchPaymentPlan.invoices = initialState.batchPaymentPlan.invoices;
        },
        applyInvoiceHaveNoPaymentPlanFiltersAction: (
            state,
            action: PayloadAction<{
                filterIndex: string;
                filterValue: any;
            }>
        ) => {
            const { filterIndex, filterValue } = action.payload;

            const filters: any = clone(state.batchPaymentPlan.invoices.panelFilters);

            if (Object.prototype.hasOwnProperty.call(filters, filterIndex)) {
                filters[filterIndex] = filterValue;
            }

            state.batchPaymentPlan.invoices.panelFilters = filters;
            state.batchPaymentPlan.invoices.pageData.currentPage = -1;
            state.batchPaymentPlan.invoices.pageData.hasNextPage = true;
        },
        getInvoicesHaveNoPaymentPlanSuccessAction: (
            state,
            action: PayloadAction<{
                data: Invoice[];
                pageData: PageData;
            }>
        ) => {
            const { data, pageData } = action.payload;

            if (action.payload.pageData.currentPage === 0) {
                state.batchPaymentPlan.invoices.data = data;
            } else {
                state.batchPaymentPlan.invoices.data = uniqWith(
                    concat(state.batchPaymentPlan.invoices.data, data),
                    isEqual
                );
            }

            state.batchPaymentPlan.invoices.loading = false;
            state.batchPaymentPlan.invoices.pageData = pageData;
        },
        updateBatchPaymentPlanReviewingInvoicesDataAction: (
            state,
            action: PayloadAction<Invoice[]>
        ) => {
            state.batchPaymentPlan.reviewingInvoice.loading = false;
            state.batchPaymentPlan.reviewingInvoice.data = action.payload;
        },
        resetCustomerHaveNoPaymentPlanFiltersAction: (
            state,
            _action: PayloadAction
        ) => {
            state.batchPaymentPlan.customers = { ...initialState.batchPaymentPlan.customers };
        },
        applyCustomerHaveNoPaymentPlanFiltersAction: (
            state,
            action: PayloadAction<{
                filterIndex: string;
                filterValue: any;
            }>
        ) => {
            const { filterIndex, filterValue } = action.payload;

            const filters: any = clone(state.batchPaymentPlan.customers.panelFilters);

            if (Object.prototype.hasOwnProperty.call(filters, filterIndex)) {
                filters[filterIndex] = filterValue;
            }

            state.batchPaymentPlan.customers.panelFilters = filters;
            state.batchPaymentPlan.customers.pageData.currentPage = -1;
            state.batchPaymentPlan.customers.pageData.hasNextPage = true;
        }
    }
});

const { actions, reducer } = invoiceSlice;

// Actions
export const {
    getInvoicesForOrganisationRequestAction,
    getInvoicesRequestAction,
    getInvoicesSuccessAction,
    getInvoicesErrorAction,
    updateInvoicesFiltersAction,
    updateInvoicesSortByAndStateAction,
    updateInvoicesTableFilterAction,
    clearInvoicesStateAllTableFiltersAction,
    clearInvoicesStateDataAction,
    clearInvoicesListData,
    setSelectedInvoiceIdRequestAction,
    setSelectedInvoiceIdSuccessAction,
    getOrganisationInvoiceDataRequestAction,
    getInvoiceDataRequestAction,
    getInvoiceDataSuccessAction,
    getInvoiceDataErrorAction,
    clearInvoiceDataAction,
    getOrganisationInvoiceConversationRequestAction,
    getInvoiceConversationRequestAction,
    getInvoiceConversationSuccessAction,
    getInvoiceConversationErrorAction,
    updateInvoiceConversationFiltersAction,
    updateInvoiceConversationTableFilterAction,
    updateInvoiceConversationSortByAction,
    getOrganisationInvoiceChangesRequestAction,
    getInvoiceChangesRequestAction,
    getInvoiceChangesSuccessAction,
    getInvoiceChangesErrorAction,
    updateInvoiceChangesFiltersAction,
    updateInvoiceChangesTableFilterAction,
    updateInvoiceChangesSortByAction,
    setTableCompactViewAction,
    getInvoiceChangeLinesSuccessAction,
    getCustomerInvoicesForCompanyRequestAction,
    getCustomersHaveNoPaymentPlanRequestAction,
    getInvoicesHaveNoPaymentPlanRequestAction,
    getPaymentPlanConfigTemplateRequestAction,
    updateBatchPaymentPlanFormDataAction,
    updateInvoicesHaveNoPaymentPlanDataAction,
    getCustomersHaveNoPaymentPlanFailedAction,
    getCustomersHaveNoPaymentPlanSuccessfullyAction,
    updateBatchPaymentPlanCustomerFilters,
    updateBatchPaymentPlanInvoiceFilters,
    getInvoicesHaveNoPaymentPlanSuccessfullyAction,
    getInvoicesHaveNoPaymentPlanFailedAction,
    getPaymentPlanConfigTemplateSuccessfullyAction,
    getPaymentPlanConfigTemplateFailedAction,
    resetBatchPaymentPlanStateAction,
    selectPaymentPlanTemplateAction,
    applyReviewingBatchPaymentPlanAction,
    resetInvoiceHaveNoPaymentPlanFiltersAction,
    applyInvoiceHaveNoPaymentPlanFiltersAction,
    getInvoicesHaveNoPaymentPlanSuccessAction,
    updateBatchPaymentPlanReviewingInvoicesDataAction,
    resetCustomerHaveNoPaymentPlanFiltersAction,
    applyCustomerHaveNoPaymentPlanFiltersAction
} = actions;

export const getInvoiceCustomFieldsForCompanyRequestAction = createAction(
    `${actionPrefix}/getInvoiceCustomFieldsForCompanyRequestAction`,
    function prepare(companyId: string,
        callback?: (response: DynamicObject) => void
    ) {
        return {
            payload: {
                companyId,
                callback
            }
        }
    }
);

export const getInvoiceCustomFieldsForOrganisationRequestAction = createAction(
    `${actionPrefix}/getInvoiceCustomFieldsForOrganisationRequestAction`,
    function prepare(
        CompanyIds: string[],
        FieldName?: string[],
        callback?: (response: DynamicObject) => void
    ) {
        return {
            payload: {
                CompanyIds,
                FieldName,
                callback
            }
        }
    }
);

export const checkInvoicesIfSingleCustomerAction = createAction(
    `${actionPrefix}/checkInvoicesIfSingleCustomerAction`,
    function prepare(payload: any) {
        return {
            payload
        };
    }
);

export const invoicePauseResumeRequestAction = createAction(
    `${actionPrefix}/invoicePauseResumeRequestAction`,
    function prepare(payload: InvoicePauseResumeRequestPayload) {
        return {
            payload
        };
    }
);

export const checkInvoicesPauseResumeStateAction = createAction(
    `${actionPrefix}/checkInvoicesPauseResumeStateAction`,
    function prepare(payload: GetInvoicesRequestPayload) {
        return {
            payload
        };
    }
);

export const raiseQueryInvoiceBulkRequestAction = createAction(
    `${actionPrefix}/raiseQueryInvoiceBulkRequestAction`,
    function prepare(payload: {}) {
        return {
            payload
        };
    }
);

export const invoiceAddCommentRequestAction = createAction(
    `${actionPrefix}/invoiceAddCommentRequestAction`,
    function prepare(payload: InvoiceAddCommentRequestPayload) {
        return {
            payload
        };
    }
);

export const invoicesFetchedForOrganisationAction = createAction(
    `${actionPrefix}/invoicesFetchedForOrganisationAction`,
    function prepare(payload: InvoicesFetchedForOrganisationPayload) {
        return {
            payload
        };
    }
);

export const organisationInvoiceAddCommentRequestAction = createAction(
    `${actionPrefix}/organisationInvoiceAddCommentRequestAction`,
    function prepare(
        payload: InvoiceAddCommentRequestPayload,
        region: string,
        companyIds: string[]
    ) {
        return {
            payload: { payload, region, companyIds }
        };
    }
);

export const invoiceConversationLinesFetchedForOrganisationAction = createAction(
    `${actionPrefix}/invoiceConversationLinesFetchedForOrganisationAction`,
    function prepare(payload: ConversationLinesFetchedForOrganisationPayload) {
        return {
            payload
        };
    }
);

export const invoiceChangeLinesFetchedForOrganisationAction = createAction(
    `${actionPrefix}/invoiceChangeLinesFetchedForOrganisationAction`,
    function prepare(payload: ChangeLinesFetchedForOrganisationPayload) {
        return {
            payload
        };
    }
);

export const getInvoicesForPaymentPlanRequestAction = createAction(
    `${actionPrefix}/getInvoicesForPaymentPlanRequestAction`,
    function prepare(
        payload: GetInvoicesRequestPayload,
        callback: (response: DynamicObject) => void
    ) {
        return {
            payload: { payload, callback}
        };
    }
);

export const updateBatchPaymentPlanFormDataRequestAction = createAction(
    `${actionPrefix}/updateBatchPaymentPlanFormDataRequestAction`,
    function prepare(
        formData: any,
        refetchInvoicesPayload?: any
    ) {
        return {
            payload: {
                formData,
                refetchInvoicesPayload
            }
        }
    }
);

export const submitBatchPaymentPlanAction = createAction(
    `${actionPrefix}/submitBatchPaymentPlanAction`,
    function prepare(payload: any) {
        return {
            payload
        };
    }
);

export const getCustomerInvoicesForCompanyFailedAction = createAction(
    `${actionPrefix}/getCustomerInvoicesForCompanyFailedAction`
);

// Selectors
export const getInvoicesState = (state: RootState) => state.invoices;
export const getInvoicesPageData = createSelector(
    getInvoicesState,
    (invoices) => invoices.pageData
);
export const getInvoicesData = createSelector(
    getInvoicesState,
    (invoices) => invoices.data
);
export const getInvoiceData = createSelector(
    getInvoicesState,
    (invoices) => invoices.activeData
);
export const getSelectedInvoiceId = createSelector(
    getInvoiceData,
    (activeData) => activeData.selectedId
);
export const getCompanyIdByInvoiceSelectedId = createSelector(
    [getInvoicesData, getSelectedInvoiceId],
    (invoices, selectedId) => {
        const selectedInvoice = invoices.find((invoice) => invoice.Id === selectedId);
        return selectedInvoice ? selectedInvoice.CompanyId : null;
    }
);
export const getBatchPaymentPlanState = createSelector(
    getInvoicesState,
    (invoices) => invoices.batchPaymentPlan
);
export const getBatchPaymentPlanFormData = createSelector(
    getInvoicesState,
    (invoices) => {
        return invoices.batchPaymentPlan.formData || undefined;
    }
);
export const getInvoiceCustomers = createSelector(
    getInvoicesData,
    (invoices) => invoices.map((invoice) => invoice.Customer)
);

// Reducer
export default reducer;
